import { Editor, Path } from 'slate';
import { ReactEditor } from 'slate-react';
import type { MouseEvent } from 'react';

export const isAtStartOfNode = (editor: Editor): boolean => {
  const { selection } = editor;

  if (!selection) return false;
  // Check if we're at [0,0] - the absolute first position
  return (
    (Path.equals(selection.focus.path, [0, 0]) ||
      Path.equals(selection.focus.path, [0, 0, 0]) ||
      Path.equals(selection.focus.path, [1, 0, 0])) &&
    selection.focus.offset === 0
  );
};

/**
 * Wraps a ReactEditor with error handling for DOM resolution errors.
 * These errors occur when Slate's virtual document is out of sync with the DOM,
 * typically during rapid editing or when nodes are deleted.
 */
export const withErrorHandling = <T extends ReactEditor>(editor: T): T => {
  const { onChange } = editor;

  editor.onChange = options => {
    try {
      onChange(options);
    } catch (error) {
      // Silently handle benign Slate DOM sync errors
      if (
        error instanceof Error &&
        (error.message.includes('Cannot resolve a DOM node') ||
          error.message.includes('Cannot resolve a DOM point') ||
          // split_node / set_node choking on text nodes that have a leftover
          // `children: null` from the backend's JSON serialization. Slate's
          // operation handler throws rather than stripping the forbidden key.
          // Catching here keeps the editor usable; the data is normalized on
          // the next save.
          error.message.includes(
            'Cannot set the "children" property of nodes'
          ) ||
          // Downstream of the above: the aborted operation leaves a partial
          // tree, and the next render / selection reconciliation reads `.text`
          // on a position that no longer resolves to a node.
          error.message.includes(
            "Cannot read properties of undefined (reading 'text')"
          ))
      ) {
        return;
      }
      // Re-throw other errors
      throw error;
    }
  };

  return editor;
};

/**
 * Guard for the Editable's `onClick`. slate-react's internal onClick runs
 * `toSlateNode` + `findPath` on the click target; if the target's DOM↔Slate
 * mapping is stale (e.g. right after a split/merge rewrites the paragraph
 * tree), `findPath` throws "Unable to find the path for Slate node". The
 * throw doesn't break the page — slate falls through and the browser still
 * places the caret — but it surfaces in Sentry on every click near the
 * rewritten region.
 *
 * We pre-run the same lookup here. On failure we return `true` so
 * `isEventHandled` short-circuits slate's internal handler before it can
 * throw. On success we return undefined so slate proceeds normally.
 */
export const createSafeSlateClickGuard =
  (editor: ReactEditor) =>
  (event: MouseEvent<HTMLDivElement>): boolean | undefined => {
    const target = event.target;
    if (!(target instanceof Node)) return undefined;
    if (!ReactEditor.hasDOMNode(editor, target)) return undefined;
    try {
      const node = ReactEditor.toSlateNode(editor, target);
      ReactEditor.findPath(editor, node);
      return undefined;
    } catch {
      return true;
    }
  };
